import React from 'react';
import { Box, Button, CircularProgress, IconButton, Paper, Radio, Stack, Tooltip, Typography } from '@mui/material';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import type {
  ExpandedFoodRow,
  FoodSearchResult,
  GenericMealOptionAddContext,
  NutritionalMealCardModel,
} from './nutritionalAnalysisTypes';
import { formatNumber } from './nutritionalAnalysisPure';
import {
  nutritionTableColumns,
  nutritionTableColumnsReadOnlyPublic,
  nutritionTableColumnsWithCheckbox,
  nutritionTableColumnsWithCheckboxReadOnlyPublic,
} from './nutritionalAnalysisConstants';
import NutritionalAnalysisNutritionRow from './NutritionalAnalysisNutritionRow';
import { nutritionItemAddingColumnSx } from '@/components/meal-planning/manual/NutritionItemAddingState';
import { MealPlanNotesControl } from '@/components/meal-planning/MealPlanNotesControl';
import type { UpdateMealPlanNotesRequest } from '@/lib/api/mealPlans';

const isFilledGenericOptionRow = (row?: ExpandedFoodRow): boolean => {
  if (!row) return false;
  return Boolean(
    String(row.item ?? '').trim()
    || String(row.itemId ?? '').trim()
    || String(row.recipeId ?? '').trim()
    || String(row.ingredientId ?? '').trim(),
  );
};

type GenericOptionGroup = {
  key: string;
  optionGroupId: string | null;
  optionGroupLabel: string;
  rows: Array<{ row: ExpandedFoodRow; index: number }>;
  firstRowIndex: number | null;
  isPlaceholder?: boolean;
};

const buildGenericMealChoiceLabel = (index: number): string => `Meal choice ${index + 1}`;

const mealCardFooterActionButtonSx = {
  textTransform: 'none',
  fontSize: '0.75rem',
  lineHeight: 1.25,
  minHeight: 30,
  py: 0.45,
  px: 1.2,
  borderColor: '#e4e7ec',
  color: '#667085',
  whiteSpace: 'nowrap',
  overflowWrap: 'normal',
  wordBreak: 'normal',
  flexShrink: 0,
};

export type NutritionalAnalysisMealCardProps = {
  meal: NutritionalMealCardModel;
  rows: ExpandedFoodRow[];
  isExpanded: boolean;
  planStatus: string;
  mealSlotDeletingName: string | null;
  isSavingMealSlot: boolean;
  onToggleExpand: () => void;
  onSaveMealSlotTemplate: () => void;
  /** Disables the "Save as Template" button (e.g. the meal slot has no recipes yet). */
  saveAsTemplateDisabled?: boolean;
  /** Tooltip explaining why "Save as Template" is disabled. */
  saveAsTemplateDisabledReason?: string;
  onEditMealSlot: () => void;
  onDeleteMealSlot: () => void;
  onOpenAddItem: (anchor: HTMLElement | null, optionContext?: GenericMealOptionAddContext) => void;
  onImportRecipes: (optionContext?: GenericMealOptionAddContext) => void;
  findEmptyRowSearchAnchor: (mealName: string, type: 'food' | 'ingredient') => HTMLElement | null;
  /** Per-row search state and handlers keyed by `rowSearchId`. */
  getRowSearchId: (row: ExpandedFoodRow, index: number) => string;
  getRowSearchResults: (rowSearchId: string) => FoodSearchResult[];
  getRowSearchLoading: (rowSearchId: string) => boolean;
  isRowAddingItem?: (mealName: string, rowSearchId: string) => boolean;
  isMealLevelAddingItem?: (mealName: string) => boolean;
  isRowDeletingItem?: (mealName: string, rowSearchId: string) => boolean;
  hasActiveRowDeletion?: boolean;
  isImportingRecipes?: boolean;
  getRowSearchOpen: (rowSearchId: string) => boolean;
  onRowItemTextChange: (mealName: string, index: number, rowSearchId: string, value: string) => void;
  onRowItemSelect: (mealName: string, index: number, rowSearchId: string, item: FoodSearchResult) => void;
  onCloseRowSearch: (rowSearchId: string) => void;
  onFocusRowSearch: (
    mealName: string,
    index: number,
    row: ExpandedFoodRow,
    rowSearchId: string,
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onAdjustRowQty: (mealName: string, index: number, delta: number) => void;
  onUpdateRowField: (mealName: string, index: number, field: keyof ExpandedFoodRow, value: string | number) => void;
  getRowQtyDraft?: (mealName: string, rowId: string | undefined) => string | undefined;
  onQtyDraftChange?: (mealName: string, index: number, rawValue: string) => void;
  isRowQuantitySyncing?: (mealName: string, rowId: string | undefined) => boolean;
  onDownloadRecipeFromRow?: (mealName: string, index: number, row: ExpandedFoodRow) => void;
  getRowDownloadLoading?: (mealName: string, index: number, row: ExpandedFoodRow) => boolean;
  onDeleteRow: (mealName: string, index: number) => void;
  onRecipeDetailsFromRow: (mealName: string, index: number, row: ExpandedFoodRow) => void;
  onOpenIngredientModalFromRow: (mealName: string, index: number, rowSearchId: string, anchor: HTMLElement) => void;
  isExistingAIItemRow: (row: ExpandedFoodRow) => boolean;
  canDragMealSlot?: boolean;
  isDragging?: boolean;
  setNodeRef?: (element: HTMLElement | null) => void;
  sortableStyle?: React.CSSProperties;
  dragHandleAttributes?: Record<string, unknown>;
  dragHandleListeners?: Record<string, unknown>;
  showSaveAsTemplate?: boolean;
  /** Generic plans: rows are rendered as explicit OR option groups. */
  showOrBetweenRows?: boolean;
  isAddingGenericOptionItem?: boolean;
  genericOptionMutationDisabled?: boolean;
  /** Leading checkbox per recipe/ingredient row (alternatives). */
  showRowCheckboxes?: boolean;
  getRowCheckboxChecked?: (mealName: string, rowIndex: number) => boolean;
  onRowCheckboxChange?: (mealName: string, rowIndex: number, checked: boolean) => void;
  /** Public / shared template viewer: simplified table (no ACTIONS column, filtered empty rows). */
  publicTemplateViewer?: boolean;
  /** Template cannot be edited (private or public): disable mutations; private keeps full chrome. */
  interactionDisabled?: boolean;
  canEditNotes?: boolean;
  isSavingNotes?: boolean;
  selectedDayNumber?: number;
  isDraftPlan?: boolean;
  onSaveMealPlanNotes?: (request: UpdateMealPlanNotesRequest) => Promise<boolean>;
};

const NutritionalAnalysisMealCard: React.FC<NutritionalAnalysisMealCardProps> = ({
  meal,
  rows,
  isExpanded,
  planStatus,
  mealSlotDeletingName,
  isSavingMealSlot,
  onToggleExpand,
  onSaveMealSlotTemplate,
  saveAsTemplateDisabled = false,
  saveAsTemplateDisabledReason,
  onEditMealSlot,
  onDeleteMealSlot,
  onOpenAddItem,
  onImportRecipes,
  findEmptyRowSearchAnchor,
  getRowSearchId,
  getRowSearchResults,
  getRowSearchLoading,
  isRowAddingItem,
  isMealLevelAddingItem,
  isRowDeletingItem,
  hasActiveRowDeletion = false,
  isImportingRecipes = false,
  getRowSearchOpen,
  onRowItemTextChange,
  onRowItemSelect,
  onCloseRowSearch,
  onFocusRowSearch,
  onAdjustRowQty,
  onUpdateRowField,
  getRowQtyDraft,
  onQtyDraftChange,
  isRowQuantitySyncing,
  onDownloadRecipeFromRow,
  getRowDownloadLoading,
  onDeleteRow,
  onRecipeDetailsFromRow,
  onOpenIngredientModalFromRow,
  isExistingAIItemRow,
  canDragMealSlot = false,
  isDragging = false,
  setNodeRef,
  sortableStyle,
  dragHandleAttributes,
  dragHandleListeners,
  showSaveAsTemplate = true,
  showOrBetweenRows = false,
  isAddingGenericOptionItem = false,
  genericOptionMutationDisabled = false,
  showRowCheckboxes = false,
  getRowCheckboxChecked,
  onRowCheckboxChange,
  publicTemplateViewer = false,
  interactionDisabled = false,
  canEditNotes = false,
  isSavingNotes = false,
  selectedDayNumber = 1,
  isDraftPlan = false,
  onSaveMealPlanNotes,
}) => {
  const useGenericOptionGroups = Boolean(showOrBetweenRows);
  const mutationDisabled = interactionDisabled || genericOptionMutationDisabled;
  const hideGenericMutationControls = useGenericOptionGroups && genericOptionMutationDisabled;
  const showTableRowCheckboxes = showRowCheckboxes && !useGenericOptionGroups;
  const showFooterAddFoodButton = !useGenericOptionGroups;
  const showFooterImportRecipesButton = !useGenericOptionGroups;
  const showFooterSaveAsTemplateButton = showSaveAsTemplate;
  const showFooterActions =
    showFooterAddFoodButton || showFooterImportRecipesButton || showFooterSaveAsTemplateButton;
  const rowGridTemplate = publicTemplateViewer
    ? (showTableRowCheckboxes ? nutritionTableColumnsWithCheckboxReadOnlyPublic : nutritionTableColumnsReadOnlyPublic)
    : (showTableRowCheckboxes ? nutritionTableColumnsWithCheckbox : nutritionTableColumns);
  const tableHeaders = publicTemplateViewer
    ? (showTableRowCheckboxes
        ? ['', 'FOOD ITEM', 'QTY', 'UNIT', 'KCAL', 'PROTEIN(g)', 'CARBS(g)', 'FAT(g)']
        : ['FOOD ITEM', 'QTY', 'UNIT', 'KCAL', 'PROTEIN(g)', 'CARBS(g)', 'FAT(g)'])
    : (showTableRowCheckboxes
        ? ['', 'FOOD ITEM', 'QTY', 'UNIT', 'KCAL', 'PROTEIN(g)', 'CARBS(g)', 'FAT(g)', 'ACTIONS']
        : ['FOOD ITEM', 'QTY', 'UNIT', 'KCAL', 'PROTEIN(g)', 'CARBS(g)', 'FAT(g)', 'ACTIONS']);
  const foodColumnHeaderIndex = showTableRowCheckboxes ? 1 : 0;

  const displayRows = React.useMemo(() => {
    if (!publicTemplateViewer) return rows;
    return rows.filter((row) => {
      const label = String(row.item ?? '').trim();
      if (label) return true;
      const hasId = Boolean(
        String(row.itemId ?? '').trim()
        || String(row.recipeId ?? '').trim()
        || String(row.ingredientId ?? '').trim(),
      );
      return hasId;
    });
  }, [publicTemplateViewer, rows]);

  const genericOptionGroups = React.useMemo<GenericOptionGroup[]>(() => {
    if (!useGenericOptionGroups) return [];
    const groups: GenericOptionGroup[] = [];
    const groupIndexById = new Map<string, number>();

    displayRows.forEach((row, index) => {
      if (!isFilledGenericOptionRow(row)) return;

      const optionGroupId = String(row.optionGroupId ?? '').trim();
      if (optionGroupId) {
        const existingIndex = groupIndexById.get(optionGroupId);
        if (existingIndex !== undefined) {
          groups[existingIndex].rows.push({ row, index });
          if (!groups[existingIndex].optionGroupLabel && row.optionGroupLabel) {
            groups[existingIndex].optionGroupLabel = row.optionGroupLabel;
          }
          return;
        }

        const group: GenericOptionGroup = {
          key: `group:${optionGroupId}`,
          optionGroupId,
          optionGroupLabel: String(row.optionGroupLabel ?? '').trim() || buildGenericMealChoiceLabel(groups.length),
          rows: [{ row, index }],
          firstRowIndex: index,
        };
        groupIndexById.set(optionGroupId, groups.length);
        groups.push(group);
        return;
      }

      groups.push({
        key: `single:${row.rowId ?? index}`,
        optionGroupId: null,
        optionGroupLabel: buildGenericMealChoiceLabel(groups.length),
        rows: [{ row, index }],
        firstRowIndex: index,
      });
    });

    if (groups.length === 0) {
      groups.push({
        key: 'empty-option-1',
        optionGroupId: null,
        optionGroupLabel: buildGenericMealChoiceLabel(0),
        rows: [],
        firstRowIndex: null,
        isPlaceholder: true,
      });
    }

    return groups.map((group, index) => ({
      ...group,
      optionGroupLabel: buildGenericMealChoiceLabel(index),
    }));
  }, [displayRows, useGenericOptionGroups]);

  const renderNutritionRow = (
    row: ExpandedFoodRow,
    index: number,
    isLastRow: boolean,
    options?: { showCheckbox?: boolean },
  ) => {
    const quantityLocked = isExistingAIItemRow(row);
    const rowSearchId = getRowSearchId(row, index);
    const shouldShowCheckbox = options?.showCheckbox ?? showTableRowCheckboxes;
    const resolvedRowId = row.rowId ?? rowSearchId;
    const isQuantitySyncing = Boolean(isRowQuantitySyncing?.(meal.name, resolvedRowId));
    const qtyDraft = getRowQtyDraft?.(meal.name, resolvedRowId);
    const hasVisibleItem = String(row.item ?? '').trim().length > 0;

    const isAddingItem =
      Boolean(isRowAddingItem?.(meal.name, resolvedRowId))
      || Boolean(!hasVisibleItem && isMealLevelAddingItem?.(meal.name));
    const isDeletingItem = Boolean(isRowDeletingItem?.(meal.name, resolvedRowId));

    return (
      <NutritionalAnalysisNutritionRow
        key={row.rowId ?? `${meal.name}-${index}`}
        mealName={meal.name}
        rowIndex={index}
        row={row}
        nutritionTableColumns={rowGridTemplate}
        showRowCheckbox={shouldShowCheckbox}
        rowCheckboxChecked={Boolean(getRowCheckboxChecked?.(meal.name, index))}
        onRowCheckboxChange={
          shouldShowCheckbox && onRowCheckboxChange
            ? (checked) => onRowCheckboxChange(meal.name, index, checked)
            : undefined
        }
        quantityLocked={quantityLocked}
        isQuantitySyncing={isQuantitySyncing}
        isAddingItem={isAddingItem}
        isDeletingItem={isDeletingItem}
        hasActiveRowDeletion={hasActiveRowDeletion}
        isSavingMealSlot={isSavingMealSlot}
        searchResults={getRowSearchResults(rowSearchId)}
        isSearching={getRowSearchLoading(rowSearchId)}
        showResults={getRowSearchOpen(rowSearchId)}
        onItemTextChange={(nextValue) => {
          onRowItemTextChange(meal.name, index, rowSearchId, nextValue);
        }}
        onSelectSearchResult={(selectedItem) => {
          onRowItemSelect(meal.name, index, rowSearchId, selectedItem);
        }}
        onCloseSearch={() => {
          onCloseRowSearch(rowSearchId);
        }}
        onFocusSearch={(event) => {
          onFocusRowSearch(meal.name, index, row, rowSearchId, event);
        }}
        onAdjustQty={(delta) => {
          onAdjustRowQty(meal.name, index, delta);
        }}
        qtyDraft={qtyDraft}
        onQtyDraftChange={(rawValue) => {
          onQtyDraftChange?.(meal.name, index, rawValue);
        }}
        onUpdateField={(field, value) => {
          onUpdateRowField(meal.name, index, field, value);
        }}
        onDownloadRow={
          onDownloadRecipeFromRow
            ? (event) => {
                event.stopPropagation();
                onDownloadRecipeFromRow(meal.name, index, row);
              }
            : undefined
        }
        isDownloadingRow={Boolean(getRowDownloadLoading?.(meal.name, index, row))}
        onEditRow={(event) => {
          event.stopPropagation();
          if (row.itemType === 'recipe') {
            onRecipeDetailsFromRow(meal.name, index, row);
            return;
          }
          onOpenIngredientModalFromRow(meal.name, index, rowSearchId, event.currentTarget as HTMLElement);
        }}
        onDeleteRow={() => void onDeleteRow(meal.name, index)}
        isLastRow={isLastRow}
        readOnly={publicTemplateViewer}
        interactionDisabled={interactionDisabled || genericOptionMutationDisabled}
      />
    );
  };

  const renderGenericOptionSearchRow = (
    optionContext: GenericMealOptionAddContext,
    label?: string,
  ) => {
    if (publicTemplateViewer) return null;
    const isAddingOptionItem = isAddingGenericOptionItem;
    const disabled = mutationDisabled || isAddingOptionItem;
    const openAddItemPopover = (anchor: HTMLElement) => {
      if (disabled) return;
      onOpenAddItem(anchor, optionContext);
    };

    return (
      <Stack spacing={label ? 1.25 : 0} sx={{ mt: label ? 1.25 : 0 }}>
        {label ? (
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.76rem',
              fontWeight: 600,
              color: '#1f2937',
            }}
          >
            {label}
          </Typography>
        ) : null}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: '#F1F8FF',
          borderRadius: '8px',
          display: 'grid',
          gridTemplateColumns: rowGridTemplate,
          alignItems: 'center',
          gap: 1,
          '& > *': {
            minWidth: 0,
          },
        }}
      >
        <Box
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label="Add food or ingredient"
          onFocus={(event) => openAddItemPopover(event.currentTarget)}
          onClick={(event) => openAddItemPopover(event.currentTarget)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            openAddItemPopover(event.currentTarget);
          }}
          sx={{
            width: '100%',
            minWidth: 0,
            px: 1,
            py: 1,
            borderRadius: '8px',
            bgcolor: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            gap: 0.85,
            minHeight: 36,
            cursor: disabled ? 'default' : 'text',
            opacity: disabled ? 0.65 : 1,
            ...(isAddingOptionItem ? nutritionItemAddingColumnSx : {}),
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              lineHeight: '20px',
              color: isAddingOptionItem ? '#667085' : '#98a2b3',
              px: 0.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Search food or ingredient...
          </Typography>
        </Box>
        <Box
          sx={{
            width: 106,
            height: 35,
            borderRadius: '999px',
            bgcolor: '#ffffff',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            alignItems: 'center',
            overflow: 'hidden',
            justifySelf: 'center',
            columnGap: '4px',
            opacity: 0.55,
          }}
          aria-hidden
        >
          <Box sx={{ height: '100%', bgcolor: '#ededed', display: 'grid', placeItems: 'center' }}>
            <Typography sx={{ textAlign: 'center', fontSize: '1rem', color: '#5b6470', lineHeight: 1 }}>-</Typography>
          </Box>
          <Box sx={{ height: '100%', bgcolor: '#ffffff', display: 'grid', placeItems: 'center' }}>
            <Typography sx={{ fontSize: '14px', color: '#364153', lineHeight: '20px' }}>1</Typography>
          </Box>
          <Box sx={{ height: '100%', bgcolor: '#ededed', display: 'grid', placeItems: 'center' }}>
            <Typography sx={{ textAlign: 'center', fontSize: '1rem', color: '#5b6470', lineHeight: 1 }}>+</Typography>
          </Box>
        </Box>
        <Box
          sx={{
            px: 0.75,
            height: 35,
            borderRadius: '4px',
            border: '1px solid #eaeaea',
            bgcolor: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: 104,
            minWidth: 104,
            maxWidth: 104,
            justifySelf: 'center',
            opacity: 0.55,
          }}
          aria-hidden
        >
          <Typography
            sx={{
              fontSize: '14px',
              color: '#364153',
              lineHeight: '20px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            -
          </Typography>
          <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18, color: '#1f2937', flexShrink: 0 }} />
        </Box>
        {['0', '0', '0', '0'].map((value, index) => (
          <Typography
            key={`empty-generic-macro-${index}`}
            sx={{
              justifySelf: 'center',
              width: index === 0 ? 68 : index === 3 ? 44 : 56,
              textAlign: 'center',
              p: 0,
              fontSize: '14px',
              color: index === 0 ? '#f59e0b' : '#111827',
              fontWeight: 600,
              lineHeight: '20px',
              opacity: 0.55,
            }}
          >
            {value}
          </Typography>
        ))}
        <Box aria-hidden sx={{ minHeight: 24 }} />
      </Box>
      </Stack>
    );
  };

  const renderGenericOptionImportButton = (optionContext: GenericMealOptionAddContext) => {
    if (publicTemplateViewer) return null;
    return (
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
        <Button
          disabled={mutationDisabled || isImportingRecipes}
          onClick={(event) => {
            event.stopPropagation();
            onImportRecipes(optionContext);
          }}
          variant="outlined"
          startIcon={isImportingRecipes ? <CircularProgress size={12} sx={{ color: '#667085' }} /> : undefined}
          sx={{
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 700,
            minHeight: 30,
            px: 1.2,
            py: 0.4,
            borderColor: '#d9e2ec',
            color: '#667085',
            bgcolor: '#ffffff',
            '&:hover': { borderColor: '#93c5fd', bgcolor: '#eff6ff' },
          }}
        >
          + Import from Recipes
        </Button>
      </Stack>
    );
  };

  const nextGenericOptionContext: GenericMealOptionAddContext = {
    optionGroupLabel: buildGenericMealChoiceLabel(genericOptionGroups.length),
    firstRowIndex: null,
    isNewOption: true,
  };

  return (
  <Paper
    elevation={0}
    ref={setNodeRef}
    style={sortableStyle}
    sx={{
      borderRadius: '8px',
      border: '1px solid #dbe4ec',
      overflow: 'hidden',
      bgcolor: '#ffffff',
      boxShadow: 'none',
      opacity: isDragging ? 0.85 : 1,
      transition: 'opacity 140ms ease',
    }}
  >
    <Box
      onClick={onToggleExpand}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onToggleExpand();
      }}
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      aria-label={isExpanded ? `Collapse ${meal.name}` : `Expand ${meal.name}`}
      sx={{
        px: 2,
        py: 1.25,
        minHeight: 44,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        flexWrap: { xs: 'wrap', lg: 'nowrap' },
        background: 'linear-gradient(90deg, #32B8DE 0%, #20AEDC 100%)',
        borderBottom: isExpanded ? '1px solid rgba(255, 255, 255, 0.22)' : 'none',
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ minWidth: 0, flex: 1, flexWrap: { xs: 'wrap', md: 'nowrap' }, rowGap: 0.5 }}
      >
        {!publicTemplateViewer ? (
          <Box
            {...(canDragMealSlot ? dragHandleAttributes : {})}
            {...(canDragMealSlot ? dragHandleListeners : {})}
            onClick={(event) => event.stopPropagation()}
            sx={{
              display: 'grid',
              placeItems: 'center',
              cursor: canDragMealSlot ? (isDragging ? 'grabbing' : 'grab') : 'default',
              opacity: canDragMealSlot ? 1 : 0.65,
            }}
            aria-label={canDragMealSlot ? `Reorder ${meal.name}` : undefined}
          >
            <DragIndicatorRoundedIcon sx={{ fontSize: 16, color: '#ffffff', flexShrink: 0 }} />
          </Box>
        ) : (
          <Box sx={{ width: 8, flexShrink: 0 }} aria-hidden />
        )}
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontWeight: 600,
              lineHeight: '20px',
              color: '#ffffff',
              whiteSpace: { xs: 'normal', lg: 'nowrap' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: { xs: '100%', md: 220, xl: 'none' },
            }}
          >
            {meal.name}
          </Typography>
         
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ py: 0.25 }}>
          <AccessTimeRoundedIcon sx={{ fontSize: 12, color: '#ffffff' }} />
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: '16px',
              color: '#ffffff',
              whiteSpace: { xs: 'normal', lg: 'nowrap' },
            }}
          >
            {meal.time}
          </Typography>
        </Stack>
        {!publicTemplateViewer ? (
          <IconButton
            type="button"
            size="small"
            disabled={interactionDisabled || Boolean(mealSlotDeletingName) || isSavingMealSlot}
            onClick={(event) => {
              event.stopPropagation();
              onEditMealSlot();
            }}
            aria-label={`Edit ${meal.name} meal slot`}
            sx={{
              p: 0.25,
              color: '#ffffff',
              flexShrink: 0,
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.12)' },
              '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.45)' },
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        ) : null}
        {(canEditNotes || meal.notes?.trim()) ? (
          <Box onClick={(event) => event.stopPropagation()} onPointerDown={(event) => event.stopPropagation()}>
            <MealPlanNotesControl
              value={meal.notes}
              disabled={isSavingNotes}
              editable={Boolean(canEditNotes && onSaveMealPlanNotes)}
              variant="icon"
              tone="onDark"
              size="sm"
              label="Meal note"
              emptyLabel="Add a note for this meal"
              helperText="Optional. Tips for this meal only."
              placeholder="Enter meal notes"
              onSave={async (notes) => {
                if (!onSaveMealPlanNotes) return false;
                return onSaveMealPlanNotes({
                  level: 'meal',
                  day: selectedDayNumber,
                  mealName: meal.apiMealName || meal.name,
                  notes,
                });
              }}
            />
          </Box>
        ) : null}
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{
          flexShrink: 0,
          width: { xs: '100%', lg: 'auto' },
          justifyContent: { xs: 'flex-start', lg: 'flex-end' },
          flexWrap: { xs: 'wrap', lg: 'nowrap' },
          rowGap: 0.5,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '12px',
            fontWeight: 600,
            lineHeight: '16px',
            color: '#ffffff',
            whiteSpace: { xs: 'normal', lg: 'nowrap' },
          }}
        >
          {meal.kcalLabel}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: '16px',
            color: '#ffffff',
            whiteSpace: { xs: 'normal', lg: 'nowrap' },
            wordBreak: 'break-word',
          }}
        >
          {`${meal.proteinLabel} ${meal.carbsLabel} ${meal.fatLabel}`}
        </Typography>
        {!publicTemplateViewer ? (
          <IconButton
            type="button"
            size="small"
            disabled={interactionDisabled || Boolean(mealSlotDeletingName) || planStatus !== 'draft'}
            onClick={(event) => {
              event.stopPropagation();
              void onDeleteMealSlot();
            }}
            aria-label={`Delete ${meal.name} meal slot`}
            sx={{
              p: 0.35,
              color: '#ffffff',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.12)' },
              '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.45)' },
            }}
          >
            {mealSlotDeletingName === meal.name ? (
              <Typography sx={{ fontSize: '0.75rem', color: '#ffffff', width: 20, textAlign: 'center' }}>…</Typography>
            ) : (
              <DeleteOutlineRoundedIcon sx={{ fontSize: 20 }} />
            )}
          </IconButton>
        ) : null}
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onToggleExpand();
          }}
          aria-label={isExpanded ? `Collapse ${meal.name}` : `Expand ${meal.name}`}
          sx={{ p: 0.25, color: '#ffffff', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.12)' } }}
        >
          {isExpanded ? (
            <KeyboardArrowUpRoundedIcon sx={{ fontSize: 16 }} />
          ) : (
            <KeyboardArrowDownRoundedIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Stack>
    </Box>

    {isExpanded ? (
      <>
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: publicTemplateViewer ? (showTableRowCheckboxes ? 720 : 680) : (showTableRowCheckboxes ? 804 : 760) }}>
            <Box
              sx={{
                bgcolor: publicTemplateViewer ? '#f8fafc' : '#ffffff',
                borderBottom: '1px solid #edf2f7',
              }}
            >
              <Box sx={{ px: publicTemplateViewer ? 1 : 0.7, py: publicTemplateViewer ? 1 : 0.95 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: rowGridTemplate,
                    gap: publicTemplateViewer ? 1 : 0.7,
                    alignItems: 'center',
                    px: 2,
                  }}
                >
                  {tableHeaders.map((h, index) => (
                    <Typography
                      key={`${meal.name}-${h}-${index}`}
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: publicTemplateViewer ? '0.75rem' : '0.75rem',
                        fontWeight: 600,
                        color: '#111827',
                        letterSpacing: publicTemplateViewer ? '0.03em' : undefined,
                        textTransform: publicTemplateViewer ? 'uppercase' : undefined,
                        whiteSpace: 'nowrap',
                        textAlign: index === foodColumnHeaderIndex ? 'left' : 'center',
                        justifySelf: index === foodColumnHeaderIndex ? 'start' : 'center',
                        ...(index === foodColumnHeaderIndex ? { pl: 1 } : {}),
                      }}
                    >
                      {h}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Box>

            <Box sx={{ px: publicTemplateViewer ? 1 : 0.7, py: publicTemplateViewer ? 1 : 0.7, bgcolor: publicTemplateViewer ? '#f8fafc' : 'transparent' }}>
              {useGenericOptionGroups ? (
                <Stack spacing={1.2}>
                  {genericOptionGroups.map((group, groupIndex) => {
                    const hasRows = group.rows.length > 0;
                    const selectedRowIndex = group.firstRowIndex ?? -1;
                    const isSelected = selectedRowIndex >= 0
                      ? Boolean(getRowCheckboxChecked?.(meal.name, selectedRowIndex))
                      : false;
                    const addSearchId = `${meal.name}::${group.key}::${hasRows ? 'and' : 'first'}`;
                    const optionContext: GenericMealOptionAddContext = {
                      optionGroupId: group.optionGroupId,
                      optionGroupLabel: group.optionGroupLabel,
                      firstRowIndex: group.firstRowIndex,
                      isNewOption: group.isPlaceholder || !hasRows,
                    };

                    return (
                      <React.Fragment key={group.key}>
                        <Box
                          sx={{
                            border: '1px solid #dbe4ec',
                            borderRadius: '8px',
                            bgcolor: '#ffffff',
                            overflow: 'hidden',
                          }}
                        >
                          {hasRows ? (
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              sx={{
                                px: 1.2,
                                py: 0.8,
                                borderBottom: '1px solid #e4e7ec',
                                bgcolor: '#ffffff',
                              }}
                            >
                              <Radio
                                size="small"
                                name={`generic-meal-choice-${meal.name}`}
                                value={group.optionGroupId ?? group.key}
                                checked={isSelected}
                                disabled={interactionDisabled}
                                onChange={() => {
                                  if (selectedRowIndex >= 0) {
                                    onRowCheckboxChange?.(meal.name, selectedRowIndex, true);
                                  }
                                }}
                                inputProps={{ 'aria-label': `Use ${group.optionGroupLabel} for nutrition` }}
                                sx={{ p: 0.35, color: '#98a2b3', '&.Mui-checked': { color: '#2563eb' } }}
                              />
                              <Typography
                                sx={{
                                  fontFamily: 'Inter, sans-serif',
                                  fontSize: '0.84rem',
                                  fontWeight: 800,
                                  color: '#1f2937',
                                }}
                              >
                                {group.optionGroupLabel}
                              </Typography>
                            </Stack>
                          ) : null}
                          <Box sx={{ p: 1, bgcolor: '#ffffff' }}>
                            {hasRows ? (
                              group.rows.map(({ row, index }, itemIndex) => (
                                <React.Fragment key={row.rowId ?? `${group.key}-${index}`}>
                                  {itemIndex > 0 ? (
                                    <Box
                                      role="presentation"
                                      sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.8 }}
                                    >
                                      <Box sx={{ flex: 1, height: '1px', bgcolor: '#dbeafe' }} />
                                      <Typography
                                        sx={{
                                          px: 1,
                                          py: 0.25,
                                          borderRadius: '7px',
                                          border: '1px solid #bfdbfe',
                                          fontFamily: 'Inter, sans-serif',
                                          fontSize: '0.68rem',
                                          fontWeight: 800,
                                          color: '#1d4ed8',
                                          letterSpacing: 0,
                                          lineHeight: 1,
                                        }}
                                      >
                                        AND
                                      </Typography>
                                      <Box sx={{ flex: 1, height: '1px', bgcolor: '#dbeafe' }} />
                                    </Box>
                                  ) : null}
                                  {renderNutritionRow(row, index, itemIndex === group.rows.length - 1, { showCheckbox: false })}
                                </React.Fragment>
                              ))
                            ) : null}
                            {!hideGenericMutationControls ? (
                              <>
                                {renderGenericOptionSearchRow(
                                  optionContext,
                                  hasRows ? '+ Add AND item' : undefined,
                                )}
                                {renderGenericOptionImportButton(optionContext)}
                              </>
                            ) : null}
                          </Box>
                        </Box>
                        {groupIndex < genericOptionGroups.length - 1 ? (
                          <Box
                            role="presentation"
                            sx={{ display: 'flex', alignItems: 'center', gap: 1.2, px: 0.5, py: 0.35 }}
                          >
                            <Box sx={{ flex: 1, height: '2px', bgcolor: '#bfdbfe' }} />
                            <Typography
                              sx={{
                                px: 1.35,
                                py: 0.38,
                                borderRadius: '999px',
                                border: '1px solid #93c5fd',
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '0.78rem',
                                fontWeight: 800,
                                color: '#1d4ed8',
                                letterSpacing: 0,
                                lineHeight: 1,
                              }}
                            >
                              OR
                            </Typography>
                            <Box sx={{ flex: 1, height: '2px', bgcolor: '#bfdbfe' }} />
                          </Box>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                  {genericOptionGroups.some((group) => group.rows.length > 0) && !hideGenericMutationControls ? (
                    <React.Fragment>
                      {renderGenericOptionSearchRow(
                        nextGenericOptionContext,
                        '+ Add alternative meal choice',
                      )}
                      {renderGenericOptionImportButton(nextGenericOptionContext)}
                    </React.Fragment>
                  ) : null}
                </Stack>
              ) : (
                displayRows.map((row, index) =>
                  renderNutritionRow(row, index, index === displayRows.length - 1, { showCheckbox: showTableRowCheckboxes }),
                )
              )}
            </Box>
          </Box>
        </Box>

        {!publicTemplateViewer && !hideGenericMutationControls && showFooterActions ? (
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ gap: 1, px: 1.3, py: 1.2, borderBottom: '1px solid #edf2f7' }}>
            {showFooterAddFoodButton ? (
              <Button
                disabled={mutationDisabled || isAddingGenericOptionItem || isImportingRecipes}
                onClick={(event) => {
                  event.stopPropagation();
                  const emptyFoodRowAnchor = findEmptyRowSearchAnchor(meal.name, 'food');
                  const emptyIngredientRowAnchor = findEmptyRowSearchAnchor(meal.name, 'ingredient');
                  onOpenAddItem(emptyFoodRowAnchor ?? emptyIngredientRowAnchor ?? event.currentTarget);
                }}
                sx={{ textTransform: 'none', color: '#20AEDC', fontSize: '0.75rem', minWidth: 0, p: 0, whiteSpace: 'nowrap', overflowWrap: 'normal' }}
              >
                + Add food or ingredient
              </Button>
            ) : <Box />}
            <Stack direction="row" spacing={0.8} sx={{ flexShrink: 0 }}>
              {showFooterSaveAsTemplateButton ? (
                <Tooltip
                  title={saveAsTemplateDisabled && saveAsTemplateDisabledReason ? saveAsTemplateDisabledReason : ''}
                  arrow
                  placement="top"
                  disableHoverListener={!saveAsTemplateDisabled || !saveAsTemplateDisabledReason}
                >
                  {/* span keeps the tooltip working while the button is disabled */}
                  <Box component="span" sx={{ display: 'inline-flex' }}>
                    <Button
                      onClick={(event) => {
                        event.stopPropagation();
                        onSaveMealSlotTemplate();
                      }}
                      disabled={
                        saveAsTemplateDisabled
                        || mutationDisabled
                        || Boolean(mealSlotDeletingName)
                        || isSavingMealSlot
                      }
                      variant="outlined"
                      sx={mealCardFooterActionButtonSx}
                    >
                      Save as Template
                    </Button>
                  </Box>
                </Tooltip>
              ) : null}
              {showFooterImportRecipesButton ? (
                <Button
                  disabled={mutationDisabled || isImportingRecipes}
                  onClick={(event) => {
                    event.stopPropagation();
                    onImportRecipes();
                  }}
                  variant="outlined"
                  startIcon={isImportingRecipes ? <CircularProgress size={12} sx={{ color: '#667085' }} /> : undefined}
                  sx={mealCardFooterActionButtonSx}
                >
                  + Import from Recipes
                </Button>
              ) : null}
            </Stack>
          </Stack>
        ) : null}

        <Typography
          sx={{
            px: publicTemplateViewer ? 2 : 1.3,
            py: publicTemplateViewer ? 1.35 : 1.2,
            fontFamily: 'Inter, sans-serif',
            fontSize: publicTemplateViewer ? '0.8rem' : '0.84rem',
            color: '#111827',
            fontWeight: 600,
            bgcolor: publicTemplateViewer ? '#f1f5f9' : '#ffffff',
            borderTop: publicTemplateViewer ? '1px solid #e2e8f0' : 'none',
          }}
        >
          Total: <Box component="span" sx={{ color: '#f59e0b', fontWeight: 600 }}>{meal.kcalLabel}</Box>, Protein: {formatNumber(meal.totals.protein)}g, Carbs: {formatNumber(meal.totals.carbs)}g, Fat: {formatNumber(meal.totals.fat)}g
        </Typography>
      </>
    ) : null}
  </Paper>
  );
};

export default NutritionalAnalysisMealCard;
